/**
 * EmailDraftRenderer — workspace renderer for `email_draft` artifacts.
 *
 * Renders subject, recipient rows (To/Cc/Bcc), and body inside an
 * `ArtifactFrame`. Body honours `body_format`:
 *   - "markdown" -> rendered via react-markdown + remark-gfm
 *   - "html"     -> sanitized via DOMPurify and inserted as HTML
 *
 * Default actions when artifact.actions is empty:
 *   - copy            (id="copy")
 *   - send_via_m365   (id="send_via_m365", primary, requires confirmation)
 *
 * The actual M365 send integration is Phase 5.4 - this renderer just
 * declares the action; the dispatcher calls back via `onAction`.
 *
 * Contract: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import DOMPurify from "dompurify"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type { Artifact, ArtifactAction, EmailDraftPayload } from "@/api/models"

import { cn } from "@/lib/utils"

import { ArtifactFrame } from "./ArtifactFrame"

const DEFAULT_ACTIONS: ArtifactAction[] = [
  { id: "copy", label: "Copy", primary: false },
  {
    id: "send_via_m365",
    label: "Send via Outlook",
    icon: "send",
    primary: true,
    requires_confirmation: true,
  },
]

export interface EmailDraftRendererProps {
  artifact: Artifact
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

function RecipientRow({ label, addresses }: { label: string; addresses: string[] }) {
  if (addresses.length === 0) return null
  return (
    <div
      className="flex items-baseline gap-2 text-sm"
      data-testid={`email-draft-${label.toLowerCase()}-row`}
    >
      <span className="text-muted-foreground w-8 shrink-0 text-xs font-medium tracking-wide uppercase">
        {label}:
      </span>
      <ul className="flex flex-wrap items-center gap-1">
        {addresses.map((addr) => (
          <li
            key={addr}
            className="bg-background/80 inline-flex items-center rounded-full border px-2 py-0.5 text-xs"
            data-testid="email-draft-recipient-chip"
          >
            {addr}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Renders an HTML email body. The string MUST already be sanitized by
 * DOMPurify before reaching this component - never pass raw model output.
 */
function SanitizedHtmlBody({ html }: { html: string }) {
  // html is the output of DOMPurify.sanitize(...) - safe to inject.
  const dangerousProps = { __html: html }
  return (
    <div
      className="prose dark:prose-invert max-w-none text-sm"
      dangerouslySetInnerHTML={dangerousProps}
      data-body-format="html"
      data-testid="email-draft-body"
    />
  )
}

function EmailBody({ payload }: { payload: EmailDraftPayload }) {
  const format = payload.body_format ?? "markdown"

  const sanitizedHtml = useMemo(() => {
    if (format !== "html") return null
    // DOMPurify is bundled in this project (see package.json). All HTML
    // bodies are sanitized here before they reach `dangerouslySetInnerHTML`
    // in `SanitizedHtmlBody`. Never pass raw HTML through.
    return DOMPurify.sanitize(payload.body)
  }, [format, payload.body])

  if (format === "html" && sanitizedHtml != null) {
    return <SanitizedHtmlBody html={sanitizedHtml} />
  }

  // Either explicit markdown, or - if dompurify is ever removed from the
  // project - the html branch falls through here as a graceful degradation
  // (render the raw markdown rather than ship unsanitized HTML).
  return (
    <div
      className={cn("prose dark:prose-invert max-w-none text-sm", "prose-p:my-2 prose-li:my-0")}
      data-body-format="markdown"
      data-testid="email-draft-body"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{payload.body}</ReactMarkdown>
    </div>
  )
}

export function EmailDraftRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: EmailDraftRendererProps) {
  // Type guard: this renderer is only valid for email_draft. We assume the
  // dispatcher routes correctly; if not, we still render the envelope with
  // the wrong-type payload as best we can - surface a console warning so
  // wiring bugs are visible.
  if (artifact.type !== "email_draft" || artifact.payload.type !== "email_draft") {
    // eslint-disable-next-line no-console
    console.warn("EmailDraftRenderer received non-email_draft artifact", artifact)
  }
  const payload = artifact.payload as EmailDraftPayload

  // Don't override actions when the artifact provides them; otherwise apply
  // the contract's defaults.
  const artifactWithActions: Artifact = useMemo(() => {
    const effectiveActions =
      artifact.actions && artifact.actions.length > 0 ? artifact.actions : DEFAULT_ACTIONS
    return { ...artifact, actions: effectiveActions }
  }, [artifact])

  const to = payload.to ?? []
  const cc = payload.cc ?? []
  const bcc = payload.bcc ?? []

  return (
    <ArtifactFrame
      artifact={artifactWithActions}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="flex flex-col gap-3" data-testid="email-draft-renderer">
        {/* Subject */}
        <div className="border-b pb-2">
          <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Subject
          </div>
          <h3 className="text-base leading-snug font-semibold" data-testid="email-draft-subject">
            {payload.subject}
          </h3>
        </div>

        {/* Recipient rows - only render when populated */}
        {(to.length > 0 || cc.length > 0 || bcc.length > 0) && (
          <div className="flex flex-col gap-1.5 border-b pb-3" data-testid="email-draft-recipients">
            <RecipientRow addresses={to} label="To" />
            <RecipientRow addresses={cc} label="Cc" />
            <RecipientRow addresses={bcc} label="Bcc" />
          </div>
        )}

        {/* Body */}
        <EmailBody payload={payload} />
      </div>
    </ArtifactFrame>
  )
}
